import {
    ButtonPrimary,
    ButtonSecondary,
    Checkbox,
    Colors,
    Heading,
    Icon,
    Modal,
    Text,
} from '@amelio/ui-library';
import { useState } from 'react';

import { MODAL_FOOTER, MODAL_PANEL, MODAL_STACK } from '../calibrationStyles';

import type { IconProps } from '@amelio/ui-library';

import { usePublishCalibrationAdjustments } from '@/api/calibration';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

export interface PublishAdjustmentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    cycleEvaluationId: string;
    cycleTitle: string;
    draftCount: number;
}

interface ConfirmationPoint {
    iconName: IconProps['name'];
    text: string;
}

/**
 * Four irreversibility warnings, an explicit "I understand" acknowledgement, and only then the
 * `Publish` action — publishing overwrites the cycle's previous snapshot for good and notifies
 * nobody, so the confirmation earns its own modal rather than a single click on the header button.
 */
export const PublishAdjustmentsModal = ({
    isOpen,
    onClose,
    cycleEvaluationId,
    cycleTitle,
    draftCount,
}: PublishAdjustmentsModalProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const publishAdjustments = usePublishCalibrationAdjustments(cycleEvaluationId);

    const [isAcknowledged, setIsAcknowledged] = useState(false);

    const confirmationPoints: ConfirmationPoint[] = [
        {
            iconName: 'essential-tick-circle',
            text: t('calibration.publish.confirmationDraftCount', { count: draftCount }),
        },
        {
            iconName: 'essential-info-circle',
            text: t('calibration.publish.confirmationCycle', { cycle: cycleTitle }),
        },
        {
            iconName: 'essential-warning-2',
            text: t('calibration.publish.confirmationIrreversible'),
        },
        {
            iconName: 'essential-warning-2',
            text: t('calibration.publish.confirmationNoNotification'),
        },
    ];

    const handleClose = () => {
        setIsAcknowledged(false);
        onClose();
    };

    const handlePublish = async () => {
        if (!isAcknowledged) {
            return;
        }

        try {
            const response = await publishAdjustments.mutateAsync();
            showSuccessToast({
                message: t('calibration.publish.success', { count: response.publishedCount }),
            });
            handleClose();
        } catch (error) {
            handleError({ error, data: { message: t('calibration.errors.publishFailed') } });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            className={MODAL_PANEL}
            dataTestId={'calibration-publish-modal'}
        >
            <div className={MODAL_STACK}>
                <Heading
                    level={4}
                    color={Colors.NAVY}
                >
                    {t('calibration.publish.modalTitle')}
                </Heading>

                <div className={'flex flex-col gap-3'}>
                    {confirmationPoints.map((point) => (
                        <div
                            key={point.text}
                            className={'flex items-start gap-3'}
                        >
                            <Icon
                                name={point.iconName}
                                color={Colors.PRIMARY}
                                className={'mt-0.5 flex-shrink-0'}
                                ariaHidden
                            />

                            <Text>{point.text}</Text>
                        </div>
                    ))}
                </div>

                <Checkbox
                    checked={isAcknowledged}
                    onChange={setIsAcknowledged}
                    label={t('calibration.publish.acknowledgeCheckbox')}
                    dataTestId={'calibration-publish-acknowledge'}
                />

                <div className={MODAL_FOOTER}>
                    <ButtonSecondary
                        onClick={handleClose}
                        dataTestId={'calibration-publish-cancel'}
                    >
                        {t('calibration.publish.cancel')}
                    </ButtonSecondary>

                    <ButtonPrimary
                        onClick={() => void handlePublish()}
                        disabled={!isAcknowledged || publishAdjustments.isPending}
                        loading={publishAdjustments.isPending}
                        dataTestId={'calibration-publish-confirm'}
                    >
                        {t('calibration.publish.confirm')}
                    </ButtonPrimary>
                </div>
            </div>
        </Modal>
    );
};
